import React, { useMemo, useState } from "react";
import { Button, Card, Form, Image, Row, Col } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import swal from "sweetalert";

import ConfiguratorStepper from "./ConfiguratorStepper";

import indiaFlag from "../../../assets/img/flags/india.svg";
import portugalFlag from "../../../assets/img/flags/portugal.svg";
import usaFlag from "../../../assets/img/flags/united-states.svg";
import dubaiFlag from "../../../assets/img/flags/united-arab-emirates.svg";
import canadaFlag from "../../../assets/img/flags/canada.svg";

const flagById = {
  india: indiaFlag,
  portugal: portugalFlag,
  usa: usaFlag,
  dubai: dubaiFlag,
  canada: canadaFlag,
};

const readJSON = (key, fallback = null) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const ELECTRONICS = {
  "Mother Board": ["Version 1", "Version 2"],
  Totalizer: ["Version 1", "Version 2"],
  "CSD Display": ["COB", "COG"],
  "Segment Type": ["Amber + Black", "Black + White"],
  Keypad: ["5*4"],
  Printer: ["PT102"],
  Pulser: ["Hall Effect – External", "Hall Effect – Magnetic"],
};

const MECHANICAL = {
  "Shear Valve": ["EMCO", "Infinity", "Kaanra"],
  "Inlet Filter": ["Local SS – 25Micron", "Local – 40Micron", "Local SS – 80 Micron"],
  "PD Meter": ["PTF 25"],
  "Solenoid Valve": ["ASCO Local", "ASCO International", "Infinity"],
  "Column Elbow": ["Non-VRS", "VRS", "SS"],
  "Pump Break away": ["Elaflex", "Infinity", "Kaanra"],
  Hose: ["Polyhose", "Elaflex", "OPW", "Chinabotai", "XFY"],
  "Sight flow": ["Infinity", "Kaanra", "OPW", "Elaflex"],
  "Swivel Coupling": [
    "Infinity",
    "Kaanra",
    "OPW",
    "Elaflex Single Plane",
    "Double Plane",
  ],
  "Nozzle Break away": ["Infinity", "Kaanra", "OPW"],
  Nozzle: ["Elaflex", "Kaanra", "OPW", "Hopetrol", "China Botai", "Datian"],
  Protocol: ["Petrocal", "DART", "Tatsuno", "GVR 2WR"],
};

const ELECTRICAL = {
  "Standard Package": ["Standard Package"],
  "Cable Harness": ["Standard Package"],
};

const OPTIONS = {
  IoT: ["Yes", "No"],
  EMT: ["Yes", "No"],
  "Extended Display": ["Yes", "No"],
  "VRS Monitoring": ["Yes", "No"],
  "Door Sensor": ["Electronics", "Electro mechanical"],
  "Sealing Cover": ["Yes", "No"],
  "Sealing Wire": ["Yes", "No"],
  "Hose Retraction": ["Yes", "No"],
  "Junction Box": ["Standard", "Additional"],
  Multimedia: ["Yes", "No"],
  "OPT Slider": ["Yes", "No"],
  "Voltage Monitor": ["Yes", "No"],
};

const SectionForm = ({ title, options, values, onChange }) => {
  const entries = Object.entries(options);
  return (
    <>
      <h5 className="mb-3">{title}</h5>
      <Row>
        {entries.map(([label, opts]) => (
          <Col key={label} md={6} className="mb-3">
            <Form.Group>
              <Form.Label>{label}</Form.Label>
              <Form.Select
                value={values[label] || ""}
                onChange={(e) => onChange(label, e.target.value)}
              >
                <option value="">Select</option>
                {opts.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        ))}
      </Row>
    </>
  );
};

const CustomConfigurator = (props) => {
  const [activeTab, setActiveTab] = useState("Electronics");

  const selectedCountry = readJSON("configutar:selectedCountry", null);

  const selectedCategory = sessionStorage.getItem("configutar:selectedCategory");
  const selectedModel = sessionStorage.getItem("configutar:selectedModel");
  const vrsSelection = sessionStorage.getItem("configutar:vrsSelection");
  const duTypeSelection = sessionStorage.getItem("configutar:duTypeSelection");
  const flowRateSelection = sessionStorage.getItem("configutar:flowRateSelection");

  const standardOrCustom = sessionStorage.getItem("configutar:standardOrCustom");

  const countryFlag = useMemo(() => {
    if (selectedCountry?.id && flagById[selectedCountry.id]) return flagById[selectedCountry.id];
    if (selectedCountry?.name) {
      const n = String(selectedCountry.name).toLowerCase();
      if (n.includes("india")) return indiaFlag;
      if (n.includes("portugal")) return portugalFlag;
      if (n.includes("usa")) return usaFlag;
      if (n.includes("dubai")) return dubaiFlag;
      if (n.includes("canada")) return canadaFlag;
    }
    return indiaFlag;
  }, [selectedCountry]);

  const [electronicsValues, setElectronicsValues] = useState({});
  const [mechanicalValues, setMechanicalValues] = useState({});
  const [electricalValues, setElectricalValues] = useState({});
  const [optionsValues, setOptionsValues] = useState({});

  const [tabSaved, setTabSaved] = useState({
    Electronics: false,
    Mechanical: false,
    Electrical: false,
    Options: false,
  });

  const [submitted, setSubmitted] = useState(false);

  const backToBase = () => {
    if (props?.history?.push) props.history.push("/mainqoute/configutar");
    else window.location.href = "/mainqoute/configutar";
  };

  const onChangeTabField = (fieldLabel, value) => {
    // if user changes something, that tab must be saved again
    setTabSaved((prev) => ({ ...prev, [activeTab]: false }));

    if (activeTab === "Electronics") {
      setElectronicsValues((prev) => ({ ...prev, [fieldLabel]: value }));
    } else if (activeTab === "Mechanical") {
      setMechanicalValues((prev) => ({ ...prev, [fieldLabel]: value }));
    } else if (activeTab === "Electrical") {
      setElectricalValues((prev) => ({ ...prev, [fieldLabel]: value }));
    } else if (activeTab === "Options") {
      setOptionsValues((prev) => ({ ...prev, [fieldLabel]: value }));
    }
  };

  const getActiveValues = () => {
    if (activeTab === "Electronics") return electronicsValues;
    if (activeTab === "Mechanical") return mechanicalValues;
    if (activeTab === "Electrical") return electricalValues;
    return optionsValues;
  };

  const getActiveOptions = () => {
    if (activeTab === "Electronics") return ELECTRONICS;
    if (activeTab === "Mechanical") return MECHANICAL;
    if (activeTab === "Electrical") return ELECTRICAL;
    return OPTIONS;
  };

  const isActiveTabFilled = Object.keys(getActiveOptions()).every(
    (field) => getActiveValues()[field]
  );

  const canSubmit = tabSaved.Electronics && tabSaved.Mechanical && tabSaved.Electrical && tabSaved.Options;

  const handleSaveTab = () => {
    if (!isActiveTabFilled) {
      swal("Incomplete form", `Please fill all fields in the ${activeTab} tab.`, "warning");
      return;
    }

    setTabSaved((prev) => ({ ...prev, [activeTab]: true }));
    setSubmitted(false);
    swal("Saved", `${activeTab} data saved successfully.`, "success");
  };

  const tabItems = ["Electronics", "Mechanical", "Electrical", "Options"];

  return (
    <div className="py-4 sales-tool-root">
      <ConfiguratorStepper
        activeStep={4}
        selections={{
          country: selectedCountry?.name || "",
          product: selectedCategory || "",
          model: selectedModel || "",
          standardOrCustom: standardOrCustom || "",
        }}
      />

      <Card border="light" className="shadow-sm">
        <Card.Body>
          <div className="configutar-product-header">
            <div className="configutar-country-title">
              <Image src={countryFlag} alt="" width={32} height={22} />
              <h4 className="mb-0">Custom Configurator</h4>
            </div>

            <Button variant="outline-primary" className="configutar-back-button" onClick={backToBase}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Back
            </Button>
          </div>

          <div className="mt-3">
            <div className="alert alert-primary" role="alert">
              {/* <div>
                <b>Country:</b> {selectedCountry?.name || ""}
              </div>
              <div>
                <b>Product:</b> {selectedCategory || ""}
              </div>
              <div>
                <b>Model:</b> {selectedModel || ""}
              </div> */}
              <div>
                 <b>Country:</b> {selectedCountry?.name || ""} | <b>Product:</b> {selectedCategory || ""} | <b>Model:</b> {selectedModel || ""}
                 | <b>VRS:</b> {vrsSelection || ""} | <b>DU Type:</b> {duTypeSelection || ""} | <b>Flow Rate:</b> {" "}
                {flowRateSelection || ""}
              </div>
            </div>
          </div>

          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {tabItems.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`btn btn-sm px-3 py-1 ${activeTab === t ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setActiveTab(t)}
                >
                  {t}
                </button>
              ))}
            </div>

            <div>
              <SectionForm
                title={activeTab}
                options={getActiveOptions()}
                values={getActiveValues()}
                onChange={onChangeTabField}
              />

              <div className="mt-4 d-flex flex-column gap-2" style={{ maxWidth: 250 }}>
                <Button
                  variant="outline-primary"
                  type="button"
                  size="sm"
                  className="w-100"
                  disabled={!selectedCountry || !isActiveTabFilled}
                  onClick={handleSaveTab}
                >
                  Save
                </Button>

                {canSubmit ? (
                  <>
                    <Button
                      variant="primary"
                      type="submit"
                      size="sm"
                      className="w-100"
                      disabled={!selectedCountry}
                      onClick={() => setSubmitted(true)}
                    >
                      Submit
                    </Button>

                    {submitted ? (
                      <div className="alert alert-success" role="alert">
                        Submitted
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="alert alert-secondary" role="alert">
                    Submit becomes available after saving all tabs.
                  </div>
                )}
              </div>
            </div>
          </>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CustomConfigurator;

